import {
  alpha,
  Button,
  Chip,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/pt-br";
import {
  ChevronLeft,
  ChevronRight,
  Event,
  KeyboardArrowDown,
  RestartAlt,
  Search,
} from "@mui/icons-material";
import dayjs, { Dayjs } from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
import { useMemo, useState } from "react";
import { DateFilterType } from "@/utils/interfaces";
import { ScheduleAdministrativeFilters } from "../ScheduleAdministrativeFilters";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { resolveCrmOrganizationId } from "src-next/modules/crm-clinical/communications/helpers/resolveCrmOrganizationId";
import { useCrmSetupConfiguration } from "src-next/modules/crm-clinical/setup/hooks/useCrmSetupConfiguration";
import type { CrmSetupConfigurationOption } from "src-next/modules/crm-clinical/setup/types";

/**
 * Rótulo do plano WhatsApp ativo — mesma regra de `useWhatsAppConfigurationBillingActions`.
 * Padrão: Fluxmed; quando Meta está selecionado, exibe o plano Meta.
 */
function getWhatsAppChannelPlanLabel(
  channelOption: CrmSetupConfigurationOption | null | undefined,
): string {
  const isMetaChannel = channelOption === "metaEmbeddedSignup";
  if (isMetaChannel) return "Meta (WhatsApp Business)";

  return "Número padrão Fluxmed";
}

/** Badge do plano FluxZap no header (consulta configuração CRM da organização). */
function ViewHeaderFluxZapPlanBadge() {
  const { access } = useSessionContext();
  const { organizationId: contextOrganizationId } = useOrganizationContext();

  const organizationId = useMemo(
    () =>
      resolveCrmOrganizationId(
        contextOrganizationId,
        access?.organizationId,
      ),
    [contextOrganizationId, access?.organizationId],
  );

  const { configurationStatus } = useCrmSetupConfiguration({ organizationId });
  const channelOption = configurationStatus?.selectedOption ?? null;
  const planLabel = getWhatsAppChannelPlanLabel(channelOption);

  return (
    <Chip
      size="small"
      label={planLabel}
      variant="outlined"
      sx={{
        color: "white",
        borderColor: alpha("#fff", 0.45),
        fontWeight: 600,
        height: 24,
      }}
    />
  );
}

// Opções de filtro de data disponíveis
const DATE_FILTER_OPTIONS: { value: DateFilterType; label: string }[] = [
  { value: "day", label: "Dia" },
  { value: "week", label: "Semana" },
  { value: "month", label: "Mês" },
];

interface ViewHeaderProps {
  title?: string;
  /** Apenas título no header (ex.: FluxZap), sem filtros de data nem admin. */
  titleOnly?: boolean;
  dateFilter?: Dayjs;
  filterType?: DateFilterType;
  setDateFilter?: (date: Dayjs) => void;
  setFilterType?: (type: DateFilterType) => void;
  showAdminFilters?: boolean;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  adminFiltersProps?: {
    selectedLocation: string;
    selectedPractitioner: string;
    onLocationChange: (location: string) => void;
    onPractitionerChange: (practitioner: string) => void;
    location: any;
    locationLoading: boolean;
    practitionerRole: any;
    practitioner: any;
    practitionerRoleLoading: boolean;
    onResetFilters: () => void;
    setIsSearchActive: (active: boolean) => void;
  };
  // Botões opcionais de ação
  onSearch?: () => void;
  onReset?: () => void;
  searchButtonProps?: {
    disabled?: boolean;
    tooltip?: string;
  };
  resetButtonProps?: {
    tooltip?: string;
  };
  /** Destaque do período analisado (ex.: painel administrativo). */
  dateEmphasis?: {
    caption?: string;
    label: string;
  };
  /** Alinhamento dos controles de data; default mantém layout legado (direita). */
  filtersAlign?: "start" | "center";
}

export function ViewHeader({
  title,
  titleOnly = false,
  dateFilter,
  filterType = "day",
  setDateFilter,
  setFilterType,
  showAdminFilters = false,
  minDate,
  maxDate,
  adminFiltersProps,
  onSearch,
  onReset,
  searchButtonProps,
  resetButtonProps,
  dateEmphasis,
  filtersAlign = "start",
}: ViewHeaderProps) {
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const today = dayjs().startOf("day");

  if (titleOnly) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2,
          backgroundColor: "primary800.main",
          color: "white",
          borderRadius: 2,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          <Typography variant="subtitle1">{title}</Typography>
          {title === "FluxZap" ? <ViewHeaderFluxZapPlanBadge /> : null}
        </Stack>
      </Paper>
    );
  }

  if (!dateFilter || !setDateFilter) {
    return null;
  }

  // Função para verificar se uma data é válida considerando min/maxDate e hoje
  const isDateValid = (date: Dayjs) => {
    if (!date) return false;

    // Dia atual sempre é válido
    if (date.isSame(today, "day")) return true;

    // Verifica minDate (se existir)
    if (minDate && date.isBefore(minDate, "day")) return false;

    // Verifica maxDate (se existir)
    if (maxDate && date.isAfter(maxDate, "day")) return false;

    return true;
  };

  // Função para obter o texto do range de data baseado no tipo de filtro
  const getDateRangeText = () => {
    const format = "DD [de] MMMM";
    const yearFormat = "[de] YYYY";

    switch (filterType) {
      case "day":
        return dateFilter.format(`${format} ${yearFormat}`);
      case "week": {
        // Formata o range da semana (início - fim)
        const startOfWeek = dateFilter.startOf("week");
        const endOfWeek = dateFilter.endOf("week");
        const showYear = startOfWeek.year() !== endOfWeek.year();
        const startText = showYear
          ? startOfWeek.format(`${format} ${yearFormat}`)
          : startOfWeek.format(format);
        const endText = endOfWeek.format(`${format} ${yearFormat}`);
        return `${startText} - ${endText}`;
      }
      case "month":
        return dateFilter.format("MMMM [de] YYYY");
      default:
        return dateFilter.format(`${format} ${yearFormat}`);
    }
  };

  // Handler para abrir o popover de seleção de tipo de filtro
  const handleOpenDateFilter = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  // Handler para fechar o popover
  const handleCloseDateFilter = () => {
    setAnchorEl(null);
  };

  // Handler para mudar o tipo de filtro
  const handleDateFilterChange = (type: DateFilterType) => {
    if (setFilterType) {
      setFilterType(type);
    }
    handleCloseDateFilter();
    // Se mudar para "day", abre o date picker automaticamente
    if (type === "day") {
      setDatePickerOpen(true);
    }
  };

  const open = Boolean(anchorEl);

  const navButtonSx = {
    color: "white",
    bgcolor: alpha("#fff", 0.1),
    "&:hover": { bgcolor: alpha("#fff", 0.2) },
    "&.Mui-disabled": {
      color: alpha("#fff", 0.3),
      bgcolor: alpha("#fff", 0.05),
    },
  };

  const renderFilterTypeButton = (popoverHorizontal: "left" | "center") =>
    setFilterType ? (
    <>
      <Tooltip title="Clique para alterar o tipo de visualização" arrow>
        <Button
          onClick={handleOpenDateFilter}
          variant="outlined"
          endIcon={<KeyboardArrowDown />}
          sx={{
            textTransform: "none",
            minWidth: "100px",
            color: "white",
            borderColor: alpha("#fff", 0.3),
            bgcolor: alpha("#fff", 0.1),
            "&:hover": {
              borderColor: alpha("#fff", 0.5),
              bgcolor: alpha("#fff", 0.2),
            },
          }}
        >
          {DATE_FILTER_OPTIONS.find((opt) => opt.value === filterType)?.label}
        </Button>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleCloseDateFilter}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: popoverHorizontal,
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: popoverHorizontal,
        }}
      >
        <List sx={{ width: 200, py: 0 }}>
          {DATE_FILTER_OPTIONS.map((option) => (
            <ListItemButton
              key={option.value}
              selected={filterType === option.value}
              onClick={() => handleDateFilterChange(option.value)}
            >
              <ListItemText
                primary={option.label}
                secondary={option.value === filterType ? "Selecionado" : null}
              />
            </ListItemButton>
          ))}
        </List>
      </Popover>
    </>
  ) : null;

  const periodLabel = dateEmphasis ? (
    filterType === "day" ? (
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
        <Typography
          variant="h5"
          fontWeight={700}
          onClick={() => setDatePickerOpen(true)}
          sx={{
            textAlign: "center",
            lineHeight: 1.25,
            fontSize: "1.35rem",
            cursor: "pointer",
            px: 1,
            "&:hover": { opacity: 0.92 },
          }}
        >
          {dateEmphasis.label}
        </Typography>
        <DatePicker
          open={datePickerOpen}
          onClose={() => setDatePickerOpen(false)}
          value={dateFilter}
          minDate={minDate}
          maxDate={maxDate}
          onChange={(newDate) => {
            if (newDate) {
              setDateFilter(newDate);
              setDatePickerOpen(false);
            }
          }}
          slotProps={{
            textField: {
              sx: {
                width: 0,
                height: 0,
                overflow: "hidden",
                position: "absolute",
                opacity: 0,
                pointerEvents: "none",
              },
            },
            popper: {
              sx: {
                "& .MuiPaper-root": {
                  boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                },
              },
            },
          }}
        />
      </LocalizationProvider>
    ) : (
      <Typography
        variant="h5"
        fontWeight={700}
        sx={{ textAlign: "center", lineHeight: 1.25, fontSize: "1.35rem", px: 1 }}
      >
        {dateEmphasis.label}
      </Typography>
    )
  ) : null;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        backgroundColor: "primary800.main",
        color: "white",
        borderRadius: 2,
      }}
    >
      {dateEmphasis ? (
        <Stack
          spacing={1.5}
          alignItems="center"
          sx={{ position: "relative", width: "100%" }}
        >
          {(onReset || adminFiltersProps) && (
            <Tooltip title={resetButtonProps?.tooltip || "Limpar filtros"}>
              <IconButton
                size="small"
                onClick={() => {
                  if (onReset) {
                    onReset();
                  } else {
                    adminFiltersProps?.onResetFilters?.();
                    adminFiltersProps?.setIsSearchActive?.(false);
                    setDateFilter(dayjs());
                  }
                }}
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  ...navButtonSx,
                }}
              >
                <RestartAlt />
              </IconButton>
            </Tooltip>
          )}

          {dateEmphasis.caption && (
            <Typography
              variant="caption"
              sx={{
                opacity: 0.9,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                fontWeight: 600,
              }}
            >
              {dateEmphasis.caption}
            </Typography>
          )}

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={0.5}
            sx={{ width: "100%", maxWidth: 560, px: { xs: 4, sm: 5 } }}
          >
            <IconButton
              size="medium"
              disabled={!isDateValid(dateFilter.subtract(1, filterType))}
              onClick={() => {
                const newDate = dateFilter.subtract(1, filterType);
                if (isDateValid(newDate)) {
                  setDateFilter(newDate);
                }
              }}
              sx={navButtonSx}
            >
              <ChevronLeft />
            </IconButton>

            {periodLabel}

            <IconButton
              size="medium"
              disabled={!isDateValid(dateFilter.add(1, filterType))}
              onClick={() => {
                const newDate = dateFilter.add(1, filterType);
                if (isDateValid(newDate)) {
                  setDateFilter(newDate);
                }
              }}
              sx={navButtonSx}
            >
              <ChevronRight />
            </IconButton>
          </Stack>

          {renderFilterTypeButton("center")}
        </Stack>
      ) : filtersAlign === "center" ? (
        <Stack
          spacing={1.5}
          alignItems="center"
          sx={{ position: "relative", width: "100%" }}
        >
          {title ? (
            <Typography
              variant="subtitle1"
              sx={{ alignSelf: "flex-start", width: "100%" }}
            >
              {title}
            </Typography>
          ) : null}

          {(onReset || adminFiltersProps) && (
            <Tooltip title={resetButtonProps?.tooltip || "Limpar filtros"}>
              <IconButton
                size="small"
                onClick={() => {
                  if (onReset) {
                    onReset();
                  } else {
                    adminFiltersProps?.onResetFilters?.();
                    adminFiltersProps?.setIsSearchActive?.(false);
                    setDateFilter(dayjs());
                  }
                }}
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  ...navButtonSx,
                }}
              >
                <RestartAlt />
              </IconButton>
            </Tooltip>
          )}

          {showAdminFilters && adminFiltersProps ? (
            <ScheduleAdministrativeFilters
              selectedLocation={adminFiltersProps.selectedLocation}
              selectedPractitioner={adminFiltersProps.selectedPractitioner}
              onLocationChange={adminFiltersProps.onLocationChange}
              onPractitionerChange={adminFiltersProps.onPractitionerChange}
              location={adminFiltersProps.location}
              locationLoading={adminFiltersProps.locationLoading}
              practitionerRole={adminFiltersProps.practitionerRole}
              practitioner={adminFiltersProps.practitioner}
              practitionerRoleLoading={adminFiltersProps.practitionerRoleLoading}
            />
          ) : null}

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={0.5}
            flexWrap="wrap"
            useFlexGap
            sx={{ width: "100%", maxWidth: 720, px: { xs: 4, sm: 5 } }}
          >
            {renderFilterTypeButton("center")}

            <IconButton
              size="small"
              disabled={!isDateValid(dateFilter.subtract(1, filterType))}
              onClick={() => {
                const newDate = dateFilter.subtract(1, filterType);
                if (isDateValid(newDate)) {
                  setDateFilter(newDate);
                }
              }}
              sx={navButtonSx}
            >
              <ChevronLeft />
            </IconButton>

            {filterType === "day" ? (
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="pt-br"
              >
                <DatePicker
                  open={datePickerOpen}
                  onClose={() => setDatePickerOpen(false)}
                  value={dateFilter}
                  minDate={minDate}
                  maxDate={maxDate}
                  onChange={(newDate) => {
                    if (newDate) {
                      setDateFilter(newDate);
                      setDatePickerOpen(false);
                    }
                  }}
                  format="DD [de] MMMM [de] YYYY"
                  sx={{
                    "& .MuiInputBase-root": {
                      color: "white",
                      bgcolor: alpha("#fff", 0.1),
                      "&:hover": {
                        bgcolor: alpha("#fff", 0.2),
                      },
                      "& fieldset": {
                        border: "none",
                      },
                    },
                    "& .MuiInputBase-input": {
                      textAlign: "center",
                      fontWeight: "500",
                      py: 1,
                      px: 2,
                      minWidth: "200px",
                      cursor: "pointer",
                    },
                    "& .MuiSvgIcon-root": {
                      color: "white",
                    },
                  }}
                  slotProps={{
                    textField: {
                      variant: "outlined",
                      InputProps: {
                        startAdornment: (
                          <Event sx={{ color: "white", mr: 1 }} />
                        ),
                        endAdornment: <></>,
                      },
                      onClick: () => setDatePickerOpen(true),
                    },
                    popper: {
                      sx: {
                        "& .MuiPaper-root": {
                          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "divider",
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            ) : (
              <Typography
                variant="body1"
                sx={{
                  minWidth: 200,
                  textAlign: "center",
                  color: "white",
                  fontWeight: "500",
                }}
              >
                {getDateRangeText()}
              </Typography>
            )}

            <IconButton
              size="small"
              disabled={!isDateValid(dateFilter.add(1, filterType))}
              onClick={() => {
                const newDate = dateFilter.add(1, filterType);
                if (isDateValid(newDate)) {
                  setDateFilter(newDate);
                }
              }}
              sx={navButtonSx}
            >
              <ChevronRight />
            </IconButton>

            {onSearch ? (
              <Tooltip
                title={
                  searchButtonProps?.tooltip ||
                  "Aplicar os filtros selecionados"
                }
              >
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={onSearch}
                    startIcon={<Search />}
                    size="medium"
                    disabled={searchButtonProps?.disabled}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    Buscar
                  </Button>
                </span>
              </Tooltip>
            ) : null}
          </Stack>
        </Stack>
      ) : (
      <Stack spacing={2}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          {title && (
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="subtitle1">{title}</Typography>
            </Stack>
          )}
          <Stack 
            direction="row" 
            spacing={2} 
            alignItems="center"
            sx={{ ml: title ? 0 : "auto" }}
          >
            {showAdminFilters && adminFiltersProps && (
              <>
                <ScheduleAdministrativeFilters
                  selectedLocation={adminFiltersProps.selectedLocation}
                  selectedPractitioner={adminFiltersProps.selectedPractitioner}
                  onLocationChange={adminFiltersProps.onLocationChange}
                  onPractitionerChange={adminFiltersProps.onPractitionerChange}
                  location={adminFiltersProps.location}
                  locationLoading={adminFiltersProps.locationLoading}
                  practitionerRole={adminFiltersProps.practitionerRole}
                  practitioner={adminFiltersProps.practitioner}
                  practitionerRoleLoading={
                    adminFiltersProps.practitionerRoleLoading
                  }
                />
              </>
            )}

            {renderFilterTypeButton("left")}

            {/* Botão de reset - usa onReset se fornecido, senão usa comportamento padrão */}
            {(onReset || adminFiltersProps) && (
              <Tooltip title={resetButtonProps?.tooltip || "Limpar filtros"}>
                <IconButton
                  size="small"
                  onClick={() => {
                    if (onReset) {
                      onReset();
                    } else {
                      adminFiltersProps?.onResetFilters?.();
                      adminFiltersProps?.setIsSearchActive?.(false);
                      setDateFilter(dayjs());
                    }
                  }}
                  sx={{
                    color: "white",
                    bgcolor: alpha("#fff", 0.1),
                    "&:hover": { bgcolor: alpha("#fff", 0.2) },
                  }}
                >
                  <RestartAlt />
                </IconButton>
              </Tooltip>
            )}
            <IconButton
              size="small"
              disabled={!isDateValid(dateFilter.subtract(1, filterType))}
              onClick={() => {
                const newDate = dateFilter.subtract(1, filterType);
                if (isDateValid(newDate)) {
                  setDateFilter(newDate);
                }
              }}
              sx={navButtonSx}
            >
              <ChevronLeft />
            </IconButton>
            {/* Exibe DatePicker quando o filtro é "day", ou texto formatado para outros tipos */}
            {filterType === "day" ? (
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="pt-br"
              >
                <DatePicker
                  open={datePickerOpen}
                  onClose={() => setDatePickerOpen(false)}
                  value={dateFilter}
                  minDate={minDate}
                  maxDate={maxDate}
                  onChange={(newDate) => {
                    if (newDate) {
                      setDateFilter(newDate);
                      setDatePickerOpen(false);
                    }
                  }}
                  format="DD [de] MMMM [de] YYYY"
                  sx={{
                    "& .MuiInputBase-root": {
                      color: "white",
                      bgcolor: alpha("#fff", 0.1),
                      "&:hover": {
                        bgcolor: alpha("#fff", 0.2),
                      },
                      "& fieldset": {
                        border: "none",
                      },
                    },
                    "& .MuiInputBase-input": {
                      textAlign: "center",
                      fontWeight: "500",
                      py: 1,
                      px: 2,
                      minWidth: "200px",
                      cursor: "pointer",
                    },
                    "& .MuiSvgIcon-root": {
                      color: "white",
                    },
                  }}
                  slotProps={{
                    textField: {
                      variant: "outlined",
                      InputProps: {
                        startAdornment: (
                          <Event sx={{ color: "white", mr: 1 }} />
                        ),
                        endAdornment: <></>,
                      },
                      onClick: () => setDatePickerOpen(true),
                    },
                    popper: {
                      sx: {
                        "& .MuiPaper-root": {
                          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "divider",
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            ) : (
              <Typography
                variant="body1"
                sx={{
                  minWidth: 200,
                  textAlign: "center",
                  color: "white",
                  fontWeight: "500",
                }}
              >
                {getDateRangeText()}
              </Typography>
            )}
            <IconButton
              size="small"
              disabled={!isDateValid(dateFilter.add(1, filterType))}
              onClick={() => {
                const newDate = dateFilter.add(1, filterType);
                if (isDateValid(newDate)) {
                  setDateFilter(newDate);
                }
              }}
              sx={navButtonSx}
            >
              <ChevronRight />
            </IconButton>

            {/* Botão de buscar - opcional */}
            {onSearch && (
              <Tooltip
                title={
                  searchButtonProps?.tooltip ||
                  "Aplicar os filtros selecionados"
                }
              >
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={onSearch}
                    startIcon={<Search />}
                    size="medium"
                    disabled={searchButtonProps?.disabled}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    Buscar
                  </Button>
                </span>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </Stack>
      )}
    </Paper>
  );
}
